import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type EmbeddingJobStatus,
  type EmbeddingRuntimeLogEvent,
  type EmbeddingRuntimeLogsResponse,
  getCigarEmbeddingJobRuntimeLogs,
} from '@/api/cigar-matching';

export const DEFAULT_RUNTIME_LOG_POLL_MS = 5000;

const ACTIVE_STATUSES = new Set<EmbeddingJobStatus>([
  'queued',
  'discovering',
  'running',
]);

const isActiveStatus = (status: EmbeddingJobStatus) =>
  ACTIVE_STATUSES.has(status);

const eventTime = (event: EmbeddingRuntimeLogEvent) => {
  const timestamp = new Date(event.timestamp).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const mergeRuntimeLogEvents = (
  current: EmbeddingRuntimeLogEvent[],
  incoming: EmbeddingRuntimeLogEvent[]
) => {
  const eventsById = new Map<string, EmbeddingRuntimeLogEvent>();
  current.forEach((event) => eventsById.set(event.id, event));
  (incoming || []).forEach((event) => eventsById.set(event.id, event));

  return Array.from(eventsById.values()).sort((left, right) => {
    const timeDifference = eventTime(left) - eventTime(right);
    return timeDifference || left.id.localeCompare(right.id);
  });
};

const getPollInterval = (response?: EmbeddingRuntimeLogsResponse | null) =>
  Number.isFinite(response?.poll_after_ms) && response!.poll_after_ms > 0
    ? response!.poll_after_ms
    : DEFAULT_RUNTIME_LOG_POLL_MS;

interface RuntimeLogState {
  events: EmbeddingRuntimeLogEvent[];
  response: EmbeddingRuntimeLogsResponse | null;
  isLoading: boolean;
  error: unknown;
}

export const useEmbeddingRuntimeLogs = ({
  jobId,
  status,
  expanded,
  lookbackMinutes,
  limit = 100,
}: {
  jobId: string;
  status: EmbeddingJobStatus;
  expanded: boolean;
  lookbackMinutes: number;
  limit?: number;
}) => {
  const [state, setState] = useState<RuntimeLogState>({
    events: [],
    response: null,
    isLoading: false,
    error: null,
  });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestGenerationRef = useRef(0);
  const statusRef = useRef(status);
  const loadRef = useRef<(() => void) | null>(null);
  const scopeKey = `${jobId}:${lookbackMinutes}:${limit}`;

  statusRef.current = status;

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => {
    setState({
      events: [],
      response: null,
      isLoading: false,
      error: null,
    });
  }, [scopeKey]);

  useEffect(() => {
    requestGenerationRef.current += 1;
    const generation = requestGenerationRef.current;
    clearTimer();

    if (!expanded || !jobId) {
      loadRef.current = null;
      setState((current) => ({ ...current, isLoading: false }));
      return undefined;
    }

    let inFlight = false;
    let cancelled = false;

    const schedule = (delay: number) => {
      clearTimer();
      if (
        cancelled ||
        generation !== requestGenerationRef.current ||
        !isActiveStatus(statusRef.current)
      ) {
        return;
      }
      timerRef.current = setTimeout(load, delay);
    };

    const load = async () => {
      if (inFlight || cancelled) return;
      inFlight = true;
      clearTimer();
      setState((current) => ({ ...current, isLoading: true }));

      try {
        const response = await getCigarEmbeddingJobRuntimeLogs(jobId, {
          limit,
          lookbackMinutes,
        });
        if (cancelled || generation !== requestGenerationRef.current) return;

        setState((current) => ({
          events: response.available
            ? mergeRuntimeLogEvents(current.events, response.events)
            : [],
          response,
          isLoading: false,
          error: null,
        }));
        schedule(getPollInterval(response));
      } catch (error) {
        if (cancelled || generation !== requestGenerationRef.current) return;
        setState((current) => ({ ...current, isLoading: false, error }));
        schedule(getPollInterval());
      } finally {
        inFlight = false;
      }
    };

    loadRef.current = load;
    load();

    return () => {
      cancelled = true;
      requestGenerationRef.current += 1;
      loadRef.current = null;
      clearTimer();
    };
  }, [clearTimer, expanded, jobId, limit, lookbackMinutes]);

  useEffect(() => {
    if (!isActiveStatus(status)) {
      clearTimer();
      return;
    }

    if (expanded && !timerRef.current) loadRef.current?.();
  }, [clearTimer, expanded, status]);

  const refresh = useCallback(() => loadRef.current?.(), []);

  return { ...state, refresh };
};
